import type { AriaAttributes, ReactNode } from 'react';
import type FocusTrap from 'focus-trap-react';

import type { Placement } from './constants';

export enum Size {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

export interface ModalContainerProps
  extends Pick<AriaAttributes, 'aria-label' | 'aria-labelledby' | 'aria-describedby'> {
  /**
   * Property used for testing purposes
   */
  dataCy?: string;
  /**
   * Width of the component
   */
  width: Size | string;
  /**
   * Height of the component
   */
  height?: string;
  /**
   * Minimum height of the component
   */
  minHeight?: string;
  /**
   * Maximum height of the component
   */
  maxHeight?: string;
  /**
   * Parent component within which the `BaseModal` is rendered
   */
  parentElement?: HTMLElement;
  /**
   * Function for closing the modal
   */
  close?: () => void;
  children: ReactNode;
  /**
   * Allows the use of any of the options available in [focus-trap](https://www.npmjs.com/package/focus-trap-react) `createOptions`
   */
  focusTrapOptions?: FocusTrap['props']['focusTrapOptions'];
  /**
   * If the modal elements are inert or not.
   */
  inert?: boolean;
  /**
   * If the modal has transparent background.
   */
  transparentBackground?: boolean;
  /**
   * If the modal should close on overlay click (default: true).
   */
  shouldCloseOnOverlayClick?: boolean;
  /**
   * Allows modal to be placed at the center or aside
   */
  placement?: Placement;
}

export interface BaseModalProps extends ModalContainerProps {
  /**
   * Lets the user set an ARIA role for the close button
   */
  closeButtonLabel?: string;
}
